import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { UsersTypesSortCol, TUserTypeList, TUserTypeListResponse, TUserTypeQuery, TUsersTypesQueryRequest, TUsersTypesSortCol, adaptUserTypeFromListResponse } from '../../types';
import { BACKEND_ADMIN_USERS_TYPES } from '../../backend-paths';


export const usersTypesGetApi = createAsyncThunk(
	BACKEND_ADMIN_USERS_TYPES,
	(payload: TUserTypeQuery | undefined, thunkApi) => {
		const params = payload ? toUsersTypesQueryRequest(payload) : {};

		return HttpService.get<TUserTypeListResponse>(BACKEND_ADMIN_USERS_TYPES, { params }).then(result => {
			return adaptUserTypeFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

function toUsersTypesQueryRequest(data: TUserTypeQuery): TUsersTypesQueryRequest {
	const {
		sortCol,
		...restFields
	} = data;

	return {
		sort_col: sortCol,
		...restFields
	};
}

type TInitialState = {
	isLoading: boolean,
	list: TUserTypeList,
	query: TUserTypeQuery,
	error?: HttpError,
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: UsersTypesSortCol.CREATED_AT,
	},
	error: undefined,
};

export const usersTypesSlice = createSlice({
	name: 'usersTypesSlice',
	initialState,
	reducers: {
		setUsersTypesQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setUsersTypesQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setUsersTypesQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setUsersTypesQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setUsersTypesQuerySortCol(state, action: PayloadAction<TUsersTypesSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearUsersTypes(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[usersTypesGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[usersTypesGetApi.fulfilled.type]: (state, action: PayloadAction<TUserTypeList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[usersTypesGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useUsersTypesSelector = () => useAppSelector(state => state[usersTypesSlice.name]);

export const { clearUsersTypes, setUsersTypesQueryLimit, setUsersTypesQueryOffset, setUsersTypesQuerySort, setUsersTypesQuerySortCol, setUsersTypesQuerySearch } = usersTypesSlice.actions;
